import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import {
  UserCircle2,
  CalendarClock,
  CreditCard,
  Sliders,
  ShieldCheck,
  HelpCircle,
  LogOut,
  Loader2,
  Mail,
  ExternalLink,
  Trash2,
  KeyRound,
  PlayCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import SettingsPanel from "@/components/SettingsPanel";
import Billing from "@/pages/Billing";

/**
 * My Account shell - left sub-nav with 7 sections mounted at /app/account/*
 * All previous functionality is preserved. Legacy /app/profile and /app/billing
 * redirect into this shell.
 */

const TABS = [
  { to: "/app/account/profile", label: "Profile", icon: UserCircle2, testid: "account-tab-profile" },
  { to: "/app/account/interviews", label: "Upcoming Interviews", icon: CalendarClock, testid: "account-tab-interviews" },
  { to: "/app/account/billing", label: "Billing", icon: CreditCard, testid: "account-tab-billing" },
  { to: "/app/account/settings", label: "Settings", icon: Sliders, testid: "account-tab-settings" },
  { to: "/app/account/privacy", label: "Privacy & Security", icon: ShieldCheck, testid: "account-tab-privacy" },
  { to: "/app/account/help", label: "Help", icon: HelpCircle, testid: "account-tab-help" },
];

export default function MyAccount() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || user?.email || "?")
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div data-testid="account-page" className="animate-in">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">account</div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-50">My Account</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Manage your profile, upcoming interviews, plan, and preferences from one place.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        {/* Sub-nav */}
        <aside className="lg:sticky lg:top-8 lg:self-start" data-testid="account-subnav">
          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-2 space-y-0.5">
            {TABS.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors",
                    isActive
                      ? "bg-zinc-800/80 text-zinc-50 border border-zinc-700"
                      : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60 border border-transparent",
                  ].join(" ")
                }
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                <span className="font-medium">{label}</span>
              </NavLink>
            ))}
          </div>

          <div className="mt-3 rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-2">
            <button
              data-testid="account-signout"
              onClick={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-rose-300 hover:bg-rose-500/10 border border-transparent hover:border-rose-500/25 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>

          {/* Compact identity */}
          <div className="mt-3 rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center text-xs font-semibold text-zinc-100 border border-zinc-700 flex-shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-full w-full rounded-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium text-zinc-100 truncate">{user?.name || "You"}</div>
              <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>
        </aside>

        {/* Section content */}
        <section className="min-w-0" data-testid="account-content">
          <Routes>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<ProfileSection />} />
            <Route path="interviews" element={<InterviewsSection />} />
            <Route path="billing" element={<Billing />} />
            <Route path="settings" element={<SettingsPanel />} />
            <Route path="privacy" element={<PrivacySection />} />
            <Route path="help" element={<HelpSection />} />
            <Route path="*" element={<Navigate to="profile" replace />} />
          </Routes>
        </section>
      </div>
    </div>
  );
}

/* ------------------------------ Profile ------------------------------ */
function ProfileSection() {
  const { user } = useAuth();
  const initials = (user?.name || user?.email || "?")
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div data-testid="account-profile-section" className="p-6 rounded-xl border border-zinc-800/70 bg-zinc-900/40 max-w-2xl xl:max-w-4xl">
      <div className="flex items-center gap-5">
        <div className="h-16 w-16 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center text-lg font-semibold border border-zinc-700">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="h-full w-full rounded-full object-cover" />
          ) : (
            initials
          )}
        </div>
        <div>
          <div className="text-lg font-semibold text-zinc-50">{user?.name || "Anonymous"}</div>
          <div className="text-sm text-zinc-500">{user?.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
            signed in via {user?.provider} · role: {user?.role}
          </div>
        </div>
      </div>
      <div className="mt-6 text-xs text-zinc-500 leading-relaxed">
        Update your name, password, and language preferences under{" "}
        <NavLink className="text-amber-400 hover:text-amber-300 underline underline-offset-4" to="/app/account/settings">
          Settings
        </NavLink>
        .
      </div>
    </div>
  );
}

/* ------------------------- Upcoming Interviews ------------------------- */
function InterviewsSection() {
  const [ctx, setCtx] = useState({});
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/user/interview-context");
        setCtx(data || {});
        setDate(data?.interview_date ? data.interview_date.slice(0, 16) : "");
        setCompany(data?.company || "");
        setRole(data?.role || "");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.post("/user/interview-context", {
        interview_date: date || null,
        company: company || null,
        role: role || null,
      });
      setCtx(data);
      setEditing(false);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="account-interviews-section"
      className="p-6 rounded-xl border border-zinc-800/70 bg-zinc-900/40 max-w-2xl xl:max-w-4xl space-y-4"
    >
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-1">
          your upcoming interview
        </div>
        <div className="text-sm text-zinc-400">
          We&apos;ll use these to shape your dashboard recommendations, question predictions, and Interview Day Brief.
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : editing ? (
        <div className="space-y-3">
          <Field label="Date & time">
            <input
              data-testid="account-ctx-date"
              type="datetime-local"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
            />
          </Field>
          <Field label="Company">
            <input
              data-testid="account-ctx-company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
            />
          </Field>
          <Field label="Role">
            <input
              data-testid="account-ctx-role"
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
            />
          </Field>
          {err && <div className="text-xs text-rose-300">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button
              data-testid="account-ctx-save"
              onClick={save}
              disabled={saving}
              className="text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="text-sm border border-zinc-800 text-zinc-400 rounded-lg px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <Detail label="Date" value={ctx?.interview_date ? new Date(ctx.interview_date).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : null} />
            <Detail label="Company" value={ctx?.company} />
            <Detail label="Role" value={ctx?.role} />
          </div>
          <button
            data-testid="account-ctx-edit"
            onClick={() => setEditing(true)}
            className="text-sm border border-zinc-800 hover:bg-zinc-900 text-zinc-200 rounded-lg px-4 py-2"
          >
            Edit interview details
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</div>
      {children}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-100 mt-1">
        {value || <span className="text-zinc-600">Not set</span>}
      </div>
    </div>
  );
}

/* --------------------------- Privacy & Security --------------------------- */
function PrivacySection() {
  return (
    <div data-testid="account-privacy-section" className="space-y-5 max-w-2xl xl:max-w-4xl">
      <div className="p-6 rounded-xl border border-zinc-800/70 bg-zinc-900/40">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">
          <ShieldCheck className="h-3 w-3" /> Your data, your control
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Intuiterview never trains on your data. Uploads, video/audio interview recordings, transcripts, and briefs stay private to your account
          and can be exported or permanently deleted at any time from{" "}
          <NavLink className="text-amber-400 hover:text-amber-300 underline underline-offset-4" to="/app/account/settings">
            Settings → Privacy &amp; Data
          </NavLink>
          .
        </p>
      </div>

      <div className="p-6 rounded-xl border border-zinc-800/70 bg-zinc-900/40 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-50">
          <KeyRound className="h-4 w-4 text-amber-400" /> Password &amp; sign-in
        </div>
        <p className="text-xs text-zinc-500 leading-relaxed">
          Change your password or manage OAuth providers under{" "}
          <NavLink className="text-amber-400 hover:text-amber-300 underline underline-offset-4" to="/app/account/settings">
            Settings → Account
          </NavLink>
          .
        </p>
      </div>

      <div className="p-6 rounded-xl border border-zinc-800/70 bg-zinc-900/40 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-50">
          <ExternalLink className="h-4 w-4 text-amber-400" /> Policies &amp; Trust Center
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {[
            ["/trust/privacy", "Privacy Policy"],
            ["/trust/terms", "Terms of Service"],
            ["/trust/security", "Security Practices"],
            ["/trust/cookies", "Cookie Policy"],
            ["/trust/ai-transparency", "AI Transparency"],
          ].map(([to, label]) => (
            <a
              key={to}
              href={to}
              target="_blank"
              rel="noreferrer"
              data-testid={`privacy-link-${to.split("/").pop()}`}
              className="flex items-center justify-between px-3 py-2 rounded-lg border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/60 text-zinc-300 hover:text-zinc-100 transition-colors"
            >
              <span>{label}</span>
              <ExternalLink className="h-3 w-3 text-zinc-600" />
            </a>
          ))}
        </div>
      </div>

      <div className="p-6 rounded-xl border border-rose-500/25 bg-rose-500/[0.03] space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-rose-200">
          <Trash2 className="h-4 w-4" /> Delete account
        </div>
        <p className="text-xs text-zinc-500 leading-relaxed">
          Permanently remove your account and all associated data. This action cannot be undone. Available under{" "}
          <NavLink className="text-rose-300 hover:text-rose-200 underline underline-offset-4" to="/app/account/settings">
            Settings → Privacy &amp; Data
          </NavLink>
          .
        </p>
      </div>
    </div>
  );
}

/* --------------------------------- Help --------------------------------- */
function HelpSection() {
  const navigate = useNavigate();

  const replayTour = () => {
    try {
      localStorage.removeItem("intuiterview.onboarding.v1");
    } catch {
      /* ignore */
    }
    navigate("/app/dashboard");
  };

  const faqs = [
    {
      q: "How does Intuiterview predict my interview questions?",
      a: "We combine the job description, your resume, and the role's typical interview signals. Company insights and STAR stories are generated on demand, never pre-canned.",
    },
    {
      q: "What file types are supported for resumes?",
      a: "PDF, DOCX, and TXT up to 5 MB. You can also paste plain text directly.",
    },
    {
      q: "Is my data private?",
      a: "Yes. Your uploads, transcripts, and briefs stay on your account and are never used to train models. See our Trust Center for details.",
    },
    {
      q: "What's the difference between Practice Interview and Official AI Interview?",
      a: "Practice Interview is a webcam rehearsal with AI scoring, the warm-up. Official AI Interview is our flagship voice-based assessment with distinct AI personas that determines your Interview Readiness Score.",
    },
    {
      q: "Can I cancel my plan any time?",
      a: "Yes. Head to My Account → Billing and cancel with one click. You'll retain access until the end of your billing cycle.",
    },
  ];

  return (
    <div data-testid="account-help-section" className="space-y-5 max-w-2xl xl:max-w-4xl">
      <div className="p-6 rounded-xl border border-zinc-800/70 bg-zinc-900/40 space-y-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-amber-400">
          <HelpCircle className="h-3 w-3" /> Get help
        </div>
        <div className="text-sm text-zinc-300 leading-relaxed">
          Something not working, or want to send feedback? We reply within one business day.
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <a
            href="mailto:hello@intuiterview.com?subject=Intuiterview%20support"
            data-testid="help-contact-support"
            className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2"
          >
            <Mail className="h-3.5 w-3.5" /> hello@intuiterview.com
          </a>
          <a
            href="/trust"
            target="_blank"
            rel="noreferrer"
            data-testid="help-trust-center"
            className="inline-flex items-center gap-2 text-sm border border-zinc-800 hover:border-zinc-700 text-zinc-100 hover:bg-zinc-900 rounded-lg px-4 py-2"
          >
            Trust Center <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <button
            type="button"
            onClick={replayTour}
            data-testid="help-replay-tour"
            title="Restart the 30-second product tour on your Dashboard"
            className="inline-flex items-center gap-2 text-sm border border-zinc-800 hover:border-zinc-700 text-zinc-100 hover:bg-zinc-900 rounded-lg px-4 py-2 transition-colors"
          >
            <PlayCircle className="h-3.5 w-3.5" /> Replay product tour
          </button>
        </div>
      </div>

      <div className="p-6 rounded-xl border border-zinc-800/70 bg-zinc-900/40">
        <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500 mb-4">Frequently asked</div>
        <div className="divide-y divide-zinc-800/60">
          {faqs.map((f, i) => (
            <details key={i} className="group py-3" data-testid={`help-faq-${i}`}>
              <summary className="cursor-pointer flex items-start justify-between gap-3 list-none">
                <span className="text-sm text-zinc-100 font-medium">{f.q}</span>
                <span className="text-xs text-zinc-500 mt-0.5 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}
